import {
  BrowserCacheLocation,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
  type EndSessionRequest,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser'
import type {
  AuthClientKind,
  AuthFlow,
  AuthRedirectOptions,
  PersistedAuthContext,
  User,
} from '@/composables/auth/types'
import {
  ACTIVE_AUTH_CONTEXT_STORAGE_KEY,
  PENDING_AUTH_CONTEXT_STORAGE_KEY,
} from '@/composables/auth/constants'
import { settingsService } from '@/services/settingsService'

const ALL_CLIENT_KINDS: AuthClientKind[] = ['external', 'workforce', 'externalPassword']

/**
 * MSAL uses one sessionStorage key for in-flight interaction across all app registrations in the tab.
 * A leftover value from another clientId (e.g. external vs workforce) makes `loginRedirect` throw
 * `interaction_in_progress` — common when switching to "Staff login" after another flow or a back navigation.
 * @see BrowserCacheManager.setInteractionInProgress in @azure/msal-browser
 */
const MSAL_INTERACTION_STATUS_SESSION_KEY = 'msal.interaction.status'

const clearMsalInteractionStatusSessionKey = () => {
  if (typeof sessionStorage === 'undefined') {
    return
  }

  try {
    sessionStorage.removeItem(MSAL_INTERACTION_STATUS_SESSION_KEY)
  } catch {
    // ignore storage access errors
  }
}

const msalClients = new Map<AuthClientKind, PublicClientApplication>()
const clientInitializationPromises = new Map<AuthClientKind, Promise<void>>()

/** Prevents concurrent `loginRedirect` calls (e.g. double-clicks), which trigger MSAL `interaction_in_progress`. */
let isLoginRedirectInProgress = false

/**
 * Bumped when the user returns to login via Back/bfcache so an in-flight `loginRedirect`
 * can ignore MSAL's later `timed_out` (default redirectNavigationTimeout is 30s).
 */
let interactiveRedirectGeneration = 0

/** Ensures only one `handleRedirectPromise` chain runs at a time (e.g. duplicate callback mounts). */
let completeRedirectInFlight: Promise<AuthenticationResult | null> | null = null

const normalizeAuthority = (authority?: string | null) => authority?.replace(/\/+$/, '').toLowerCase() ?? ''

const normalizeEmailAddress = (emailAddress?: string) => {
  const trimmed = emailAddress?.trim()
  return trimmed ? trimmed.toLowerCase() : undefined
}

export const buildSilentRequest = (
  account: AccountInfo,
  authority: string,
  scopes: string[],
  forceRefresh: boolean,
  redirectUri: string,
): SilentRequest => ({
  account,
  authority,
  scopes,
  forceRefresh,
  redirectUri,
})

/** Active auth context uses localStorage (default) so silent-token / acquireTokenSilent work across tabs. */
const readPersistedContext = (storageKey: string, storage: Storage = localStorage) => {
  const serialized = storage.getItem(storageKey)
  if (!serialized) {
    return null
  }

  try {
    return JSON.parse(serialized) as PersistedAuthContext
  } catch {
    storage.removeItem(storageKey)
    return null
  }
}

const writePersistedContext = (
  storageKey: string,
  context: PersistedAuthContext,
  storage: Storage = localStorage,
) => {
  storage.setItem(storageKey, JSON.stringify(context))
}

const clearPersistedContext = (storageKey: string, storage: Storage = localStorage) => {
  storage.removeItem(storageKey)
}

const getClientKindForFlow = (flow: AuthFlow): AuthClientKind =>
  flow === 'workforce'
    ? 'workforce'
    : flow === 'external-signin-password' || flow === 'external-signup-password'
      ? 'externalPassword'
      : 'external'

const getDefaultFlowForClient = (clientKind: AuthClientKind): AuthFlow =>
  clientKind === 'workforce'
    ? 'workforce'
    : clientKind === 'externalPassword'
      ? 'external-signin-password'
      : 'external-signin'

const deriveFlowFromAuthority = (clientKind: AuthClientKind, authority?: string | null): AuthFlow => {
  if (clientKind === 'workforce') {
    return 'workforce'
  }

  if (clientKind === 'externalPassword') {
    return 'external-signin-password'
  }

  const normalizedAuthority = normalizeAuthority(authority)
  const externalFlows: AuthFlow[] = ['external-signin', 'external-signup']

  const matchingFlow = externalFlows.find((flow) => {
    return normalizeAuthority(settingsService.getAuthConfig(flow).authority) === normalizedAuthority
  })

  return matchingFlow ?? 'external-signin'
}

const buildMsalConfiguration = (clientKind: AuthClientKind): Configuration => {
  const clientConfig = settingsService.getClientConfig(clientKind)
  const authConfiguration: Configuration['auth'] = {
    clientId: clientConfig.clientId,
    authority: clientConfig.authority,
    redirectUri: clientConfig.redirectUri,
    postLogoutRedirectUri: clientConfig.postLogoutRedirectUri,
  }

  if (clientConfig.knownAuthorities.length > 0) {
    authConfiguration.knownAuthorities = clientConfig.knownAuthorities
  }

  return {
    auth: authConfiguration,
    cache: {
      // localStorage shares MSAL token cache across tabs (silent-token iframe / acquireTokenSilent).
      // Tradeoff: cache survives tab close until logout or expiry (sessionStorage cleared per tab).
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowPlatformBroker: false,
    },
  }
}

const getMsalClient = (clientKind: AuthClientKind) => {
  const existingClient = msalClients.get(clientKind)
  if (existingClient) {
    return existingClient
  }

  const client = new PublicClientApplication(buildMsalConfiguration(clientKind))
  msalClients.set(clientKind, client)
  return client
}

const ensureClientInitialized = async (clientKind: AuthClientKind) => {
  if (!clientInitializationPromises.has(clientKind)) {
    clientInitializationPromises.set(clientKind, getMsalClient(clientKind).initialize())
  }

  await clientInitializationPromises.get(clientKind)
}

const setActiveContext = (context: PersistedAuthContext) => {
  writePersistedContext(ACTIVE_AUTH_CONTEXT_STORAGE_KEY, context)
}

const findAccount = (client: PublicClientApplication, context: PersistedAuthContext | null) => {
  const accounts = client.getAllAccounts()
  const contextAccount = context?.homeAccountId
    ? accounts.find((account) => account.homeAccountId === context.homeAccountId)
    : null

  return contextAccount ?? client.getActiveAccount() ?? accounts[0] ?? null
}

const resolveAccountContext = async () => {
  const activeContext = readPersistedContext(ACTIVE_AUTH_CONTEXT_STORAGE_KEY)

  if (activeContext) {
    await ensureClientInitialized(activeContext.clientKind)
    const activeClient = getMsalClient(activeContext.clientKind)
    const activeAccount = findAccount(activeClient, activeContext)

    if (activeAccount) {
      activeClient.setActiveAccount(activeAccount)

      const resolvedContext: PersistedAuthContext = {
        flow: activeContext.flow,
        clientKind: activeContext.clientKind,
        authority: activeContext.authority,
        homeAccountId: activeAccount.homeAccountId,
      }

      setActiveContext(resolvedContext)

      return {
        account: activeAccount,
        client: activeClient,
        context: resolvedContext,
      }
    }
  }

  for (const clientKind of settingsService.getConfiguredClientKinds()) {
    await ensureClientInitialized(clientKind)
    const client = getMsalClient(clientKind)
    const account = findAccount(client, null)

    if (account) {
      client.setActiveAccount(account)

      const flow = getDefaultFlowForClient(clientKind)
      const context: PersistedAuthContext = {
        flow,
        clientKind,
        authority: settingsService.getAuthConfig(flow).authority,
        homeAccountId: account.homeAccountId,
      }

      setActiveContext(context)

      return {
        account,
        client,
        context,
      }
    }
  }

  return null
}

const buildRedirectRequest = (flow: AuthFlow, options: AuthRedirectOptions = {}): RedirectRequest => {
  const authConfig = settingsService.getAuthConfig(flow)
  const redirectRequest: RedirectRequest = {
    authority: authConfig.authority,
    scopes: authConfig.scopes,
    redirectUri: authConfig.redirectUri,
    state: options.customState,
  }

  if (options.prompt) {
    redirectRequest.prompt = options.prompt
  }

  const loginHint = normalizeEmailAddress(options.emailAddress)
  if (loginHint) {
    redirectRequest.loginHint = loginHint
  }

  if (options.identityProvider) {
    redirectRequest.extraQueryParameters = {
      idp: options.identityProvider,
    }
  }

  return redirectRequest
}

export const initializeAuthClients = async () => {
  const clientKinds = settingsService.getConfiguredClientKinds()
  await Promise.all(clientKinds.map((clientKind) => ensureClientInitialized(clientKind)))
}

export const clearAuthSession = () => {
  clearPersistedContext(ACTIVE_AUTH_CONTEXT_STORAGE_KEY)
  clearPersistedContext(PENDING_AUTH_CONTEXT_STORAGE_KEY, sessionStorage)

  ALL_CLIENT_KINDS.forEach((clientKind) => {
    msalClients.get(clientKind)?.setActiveAccount(null)
  })
}

export const isMsalTimedOutError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null || !('errorCode' in error)) {
    return false
  }

  return (error as { errorCode?: unknown }).errorCode === 'timed_out'
}

export const shouldIgnoreAbandonedRedirectError = (
  startedGeneration: number,
  currentGeneration: number,
) => startedGeneration !== currentGeneration

/**
 * True only for Back/bfcache restore of the login page.
 * Never treat `pagehide` as an abandoned redirect: that event also fires while
 * navigating *to* Microsoft, and clearing `msal.interaction.status` then makes
 * Entra SSO bounce straight back to `/login` without completing sign-in.
 */
export const shouldResetInteractiveAuthOnPageLifecycle = (event: {
  type: string
  persisted?: boolean
}) => event.type === 'pageshow' && event.persisted === true

/**
 * Clears in-tab sign-in redirect guards. Call when the login screen is shown or
 * restored from bfcache so users can retry after Back from Microsoft-hosted UX.
 *
 * Do not call this on `pagehide` during an in-flight `loginRedirect`: that
 * removes MSAL's interaction marker and the callback cannot complete.
 *
 * Invalidates any in-flight `loginRedirect` so MSAL's navigation timeout is not
 * shown as an error after Back (bfcache keeps the promise alive).
 */
export const resetInteractiveAuthRedirectState = () => {
  isLoginRedirectInProgress = false
  interactiveRedirectGeneration += 1
  clearMsalInteractionStatusSessionKey()
}

export const beginAuthRedirect = async (flow: AuthFlow, options: AuthRedirectOptions = {}) => {
  if (isLoginRedirectInProgress) {
    console.warn('[auth] Ignoring duplicate login redirect; an interactive sign-in is already in progress.')
    return
  }

  if (!settingsService.isFlowEnabled(flow)) {
    throw new Error(`Authentication flow "${flow}" is not configured for this environment.`)
  }

  const clientKind = getClientKindForFlow(flow)
  const authConfig = settingsService.getAuthConfig(flow)

  writePersistedContext(
    PENDING_AUTH_CONTEXT_STORAGE_KEY,
    {
      flow,
      clientKind,
      authority: authConfig.authority,
    },
    sessionStorage,
  )

  isLoginRedirectInProgress = true
  const startedGeneration = interactiveRedirectGeneration

  try {
    await ensureClientInitialized(clientKind)
    clearMsalInteractionStatusSessionKey()
    await getMsalClient(clientKind).loginRedirect(buildRedirectRequest(flow, options))
  } catch (error) {
    if (shouldIgnoreAbandonedRedirectError(startedGeneration, interactiveRedirectGeneration)) {
      return
    }

    isLoginRedirectInProgress = false
    throw error
  }
  // Successful `loginRedirect` navigates away; flag is not cleared intentionally.
}

const completeAuthRedirectInternal = async (): Promise<AuthenticationResult | null> => {
  const configuredClientKinds = settingsService.getConfiguredClientKinds()
  const pendingContext = readPersistedContext(PENDING_AUTH_CONTEXT_STORAGE_KEY, sessionStorage)
  const preferredClients = pendingContext
    ? [
        pendingContext.clientKind,
        ...configuredClientKinds.filter((clientKind) => clientKind !== pendingContext.clientKind),
      ]
    : configuredClientKinds

  let lastError: unknown = null

  for (const clientKind of preferredClients) {
    await ensureClientInitialized(clientKind)

    try {
      const result = await getMsalClient(clientKind).handleRedirectPromise({
        navigateToLoginRequestUrl: false,
      })

      if (!result) {
        continue
      }

      if (result.account) {
        getMsalClient(clientKind).setActiveAccount(result.account)
      }

      const flow =
        pendingContext?.clientKind === clientKind
          ? pendingContext.flow
          : deriveFlowFromAuthority(clientKind, result.authority)

      setActiveContext({
        flow,
        clientKind,
        authority: result.authority ?? settingsService.getAuthConfig(flow).authority,
        homeAccountId: result.account?.homeAccountId,
      })
      clearPersistedContext(PENDING_AUTH_CONTEXT_STORAGE_KEY, sessionStorage)

      return result
    } catch (error) {
      lastError = error

      if (pendingContext?.clientKind === clientKind) {
        clearPersistedContext(PENDING_AUTH_CONTEXT_STORAGE_KEY, sessionStorage)
        throw error
      }
    }
  }

  if (lastError) {
    throw lastError
  }

  return null
}

export const completeAuthRedirect = (): Promise<AuthenticationResult | null> => {
  if (!completeRedirectInFlight) {
    completeRedirectInFlight = completeAuthRedirectInternal().finally(() => {
      completeRedirectInFlight = null
    })
  }

  return completeRedirectInFlight
}

export const getAuthSession = async (forceRefresh = false) => {
  const resolved = await resolveAccountContext()
  if (!resolved) {
    return null
  }

  const silentRequest = buildSilentRequest(
    resolved.account,
    resolved.context.authority,
    settingsService.getAuthConfig(resolved.context.flow).scopes,
    forceRefresh,
    settingsService.silentAuthRedirectUri,
  )

  try {
    const result = await resolved.client.acquireTokenSilent(silentRequest)

    if (result.account) {
      resolved.client.setActiveAccount(result.account)
    }

    setActiveContext({
      flow: deriveFlowFromAuthority(resolved.context.clientKind, result.authority ?? resolved.context.authority),
      clientKind: resolved.context.clientKind,
      authority: result.authority ?? resolved.context.authority,
      homeAccountId: result.account?.homeAccountId ?? resolved.account.homeAccountId,
    })

    return result
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      return null
    }

    throw error
  }
}

type SupportedIdTokenClaims = {
  preferred_username?: string
  email?: string
  emails?: string[]
  upn?: string
  sub?: string
  name?: string
  given_name?: string
  oid?: string
}

const buildUserFromAccount = (account: AccountInfo): User => {
  const claims = (account.idTokenClaims ?? {}) as SupportedIdTokenClaims
  const emailAddress = claims.email ?? claims.emails?.[0] ?? claims.preferred_username ?? claims.upn
  const username = account.username || claims.preferred_username || emailAddress || claims.sub

  return {
    isAuthenticated: true,
    username,
    emailAddress,
    displayName: account.name || claims.name || claims.given_name,
    objectId: claims.oid || claims.sub,
  }
}

export const getCurrentUserFromSession = async (): Promise<User> => {
  const resolved = await resolveAccountContext()
  if (!resolved) {
    return {
      isAuthenticated: false,
    }
  }

  return buildUserFromAccount(resolved.account)
}

export const signOutFromProvider = async () => {
  const resolved = await resolveAccountContext()
  const postLogoutRedirectUri = settingsService.authPostLogoutRedirectUri

  if (!resolved) {
    clearAuthSession()
    window.location.assign(postLogoutRedirectUri)
    return
  }

  // Workforce uses the shared Microsoft Entra session (Outlook, Azure portal, etc.).
  // End the Orbviz session locally and leave that Microsoft SSO cookie intact.
  if (resolved.context.clientKind === 'workforce') {
    await resolved.client.clearCache({ account: resolved.account })
    clearAuthSession()
    window.location.assign(postLogoutRedirectUri)
    return
  }

  const logoutRequest: EndSessionRequest = {
    account: resolved.account,
    authority: resolved.context.authority,
    postLogoutRedirectUri,
  }

  clearAuthSession()
  await resolved.client.logoutRedirect(logoutRequest)
}
